<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <li
        ref="menuItemRef"
        class="el-menu-item"
        :class="{
            'is-active': active,
            'is-disabled': disabled,
            'is-focus': focused,
            'is-root': isRoot,
            'is-multiline': multiline,
            [`multiline-limit--${multilineLimit}`]: multiline && multilineLimit
        }"
        role="menuitem"
        :tabindex="disabled ? -1 : 0"
        :title="computedTitle"
        @mousedown="handleMouseDown"
        @mouseup="handleMouseUp"
        @click="handleClick"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.enter="handleClick"
    >
        <a-tooltip
            v-if="isCollapsedRoot"
            placement="right"
        >
            <div v-if="$slots.title">
                <slot name="title" />
            </div>
            <span v-if="$slots.icon" class="el-menu__bar-icon">
                <slot name="icon" />
            </span>
        </a-tooltip>

        <template v-else>
            <span v-if="$slots.icon" class="el-menu__bar-icon">
                <slot name="icon" />
            </span>
            <span v-if="$slots.title || $slots.subtitle" class="el-menu-item__content">
                <div v-if="$slots.title" class="el-menu-item__title el-text el-text--body-24 el-text--ellipsis">
                    <slot name="title" />
                </div>
                <div v-if="$slots.subtitle" class="el-menu-item__subtitle el-text--caption el-text--ellipsis el-text--color-fixed-light">
                    <slot name="subtitle" />
                </div>
            </span>
            <div class="el-text el-text--body-24 el-text--ellipsis">
                <slot />
            </div>
        </template>

        <span
            v-if="counter"
            class="el-tag ml-8"
            :class="{
                [`el-tag--${counterType}`]: counterType,
                'is-inverse': counterInverse,
                'is-small': counterSmall
            }"
            @mouseenter.stop="emit('mouseenter', $event)"
            @mouseleave.stop="emit('mouseleave', $event)"
        >
            <span class="el-tag__text">{{ counter }}</span>
        </span>

        <span v-if="$slots['right-icon']" class="el-menu-item__right-icon mr-8 ml-16">
            <slot name="right-icon" />
        </span>
    </li>
</template>

<script setup>
import { ref, computed, inject, useSlots, onBeforeUnmount, watchEffect, nextTick } from 'vue';
import ATooltip from '@core/components/tooltip/tooltip.vue';

const emit = defineEmits(['mousedown', 'mouseup', 'mouseenter', 'mouseleave']);
const slots = useSlots();
const props = defineProps({
    index: { type: String, required: true },
    multiline: { type: Boolean, default: false },
    multilineLimit: {
        type: Number,
        validator: (val) => [2, 3].includes(val),
        default: 2,
    },
    route: { type: [String, Object], default: '' },
    disabled: Boolean,
    exact: Boolean,
    showHoverHint: { type: Boolean, default: false },
    counter: { type: Number, default: 0 },
    counterType: { type: String, default: 'info' },
    counterSmall: { type: Boolean, default: false },
    counterInverse: { type: Boolean, default: false },
});

const menuContext = inject('menuContext');
const parentMenu = inject('parentMenu', null);

const indexPath = computed(() => [...(parentMenu?.indexPath || []), props.index]);
const isRoot = computed(() => (parentMenu?.level || 1) === 1);
const isCollapsedRoot = computed(() => isRoot.value && menuContext.collapse?.value);
const menuItemRef = ref(null);
const focused = ref(false);
const isClick = ref(false);

const active = computed(() => props.index === menuContext.activeIndex?.value);

const computedTitle = computed(() => {
    if (!props.showHoverHint || isCollapsedRoot.value) return null;
    const slot = (slots.title || slots.default || []);
    return Array.isArray(slot) ? slot.map(n => n.children?.toString?.() || '').join('').trim() : '';
});

function handleClick() {
    if (props.disabled) return;
    menuContext.handleItemClick({
        index: props.index,
        indexPath: indexPath.value,
        route: props.route,
        $el: menuItemRef.value,
    });
}

function handleFocus(e) {
    if (props.disabled) return;
    if (!isClick.value && menuContext.prevFocusItem !== e.target) {
        focused.value = true;
    } else {
        isClick.value = false;
    }
    menuContext.prevFocusItem = e.target;
}

function handleBlur() {
    focused.value = false;
    isClick.value = false;
    nextTick(() => {
        if (!menuContext?.$el?.contains(document.activeElement)) {
            menuContext.prevFocusItem = null;
        }
    });
}

function handleMouseDown() {
    isClick.value = !props.disabled;
    if (!props.disabled) emit('mousedown', props.index);
}

function handleMouseUp() {
    if (!props.disabled) emit('mouseup', props.index);
}

watchEffect(() => {
    menuContext?.addItem?.({ index: props.index, indexPath: indexPath.value, $el: menuItemRef.value });
});

onBeforeUnmount(() => {
    menuContext?.removeItem?.({ index: props.index });
});
</script>
